import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import {
  startGoogleSignIn,
  startAppleWebSignIn,
  startNativeAppleSignIn,
  isIOSNative,
  persistTokenAndReload,
  readableAuthError,
} from '../utils/socialAuth';

import { API_BASE } from '../lib/apiBase';
const API = API_BASE;

// Hide social auth buttons on Android (Capacitor) only — UI-level gate.
// Web/iOS keep showing Google + Apple as before.
const isAndroidApp = (() => {
  try {
    return Capacitor.getPlatform() === 'android';
  } catch {
    return false;
  }
})();

const SignupPage = ({ useAuth }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referrerName, setReferrerName] = useState('');
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for referral code in URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      const upper = String(refCode).toUpperCase();
      if (upper.startsWith('AE_')) {
        // Acquisition Editor code: persist in localStorage (sticky across
        // sessions) and look up the AE name for the friendly banner.
        try { localStorage.setItem('sfy_ae_ref', upper); } catch { /* ignore */ }
        axios.get(`${API}/ae/${upper}/public`)
          .then(res => {
            if (res.data?.ae_id) {
              setReferrerName(`Acquisition Editor ${res.data.full_name || upper}`);
            }
          })
          .catch(() => {});
      } else {
        axios.get(`${API}/referral/check-code/${refCode}`)
          .then(res => {
            if (res.data.valid) {
              setReferrerName(res.data.referrer_name);
            }
          })
          .catch(() => {});
      }
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      // No DOB / age gate — pass undefined so backend uses its Optional default.
      await signup(email, password, undefined, referralCode || undefined);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed');
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      await startGoogleSignIn();
    } catch (err) {
      setError(readableAuthError(err, 'Google sign-in failed. Please try again.'));
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    if (appleLoading) return;
    setError('');
    setAppleLoading(true);
    try {
      if (isIOSNative()) {
        const data = await startNativeAppleSignIn();
        persistTokenAndReload(data?.access_token, data?.user?.id);
        return;
      }
      await startAppleWebSignIn();
    } catch (err) {
      const code = err?.code || err?.errorCode;
      if (code === '1001' || String(err?.message || '').toLowerCase().includes('cancel')) {
        setAppleLoading(false);
        return;
      }
      setError(readableAuthError(err, 'Apple sign-in failed. Please try again.'));
      setAppleLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="signup-page">
      <div className="bg-dark-card p-8 rounded-lg w-full max-w-md">
        <h2 className="text-3xl font-bold text-dark-text mb-2 text-center">Create Account</h2>
        <p className="text-dark-text-secondary text-sm mb-6 text-center">Join StoryfYC to start reading</p>

        {error && <div className="bg-red-500/20 text-red-400 p-3 rounded mb-4" data-testid="error-message">{error}</div>}

        {/* Referral Banner */}
        {referrerName && (
          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 p-4 rounded-lg mb-4 text-center">
            <p className="text-purple-400 font-semibold">🎁 You were invited by {referrerName}</p>
            <p className="text-dark-text-secondary text-sm mt-1">
              Sign up and buy coins to earn bonus rewards!
            </p>
          </div>
        )}

        {/* Social Login Buttons — hidden on Android (Capacitor) */}
        {!isAndroidApp && (
          <>
            <div className="space-y-3 mb-6" data-testid="social-auth-section">
              <button
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 bg-white text-gray-800 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50"
                data-testid="google-signin-button"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                Continue with Google
              </button>

              <button
                onClick={handleAppleSignIn}
                disabled={appleLoading}
                className="w-full flex items-center justify-center gap-3 bg-black text-white py-3 rounded-lg font-semibold hover:bg-gray-900 transition-colors border border-gray-700 disabled:opacity-50"
                data-testid="apple-signin-button"
              >
                {appleLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                )}
                Continue with Apple
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-dark-bg"></div>
              <span className="text-dark-text-secondary text-sm">or sign up with email</span>
              <div className="flex-1 h-px bg-dark-bg"></div>
            </div>
          </>
        )}

        {/* Email/Password Form */}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-dark-text-secondary mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-dark-bg text-dark-text px-4 py-2 rounded-lg border border-dark-card focus:ring-2 focus:ring-dark-accent outline-none"
              required
              data-testid="email-input"
            />
          </div>
          <div className="mb-6">
            <label className="block text-dark-text-secondary mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-dark-bg text-dark-text px-4 py-2 pr-12 rounded-lg border border-dark-card focus:ring-2 focus:ring-dark-accent outline-none"
                required
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-text-secondary hover:text-dark-text"
                data-testid="toggle-password"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <button
            type="submit"
            className="w-full py-3 rounded-lg font-semibold transition-all bg-dark-accent text-white hover:bg-purple-700"
            data-testid="signup-submit-button"
          >
            Sign Up
          </button>
        </form>
        <p className="text-dark-text-secondary text-center mt-4">
          Already have an account?{' '}
          <Link to="/login" className="text-dark-accent hover:underline" data-testid="login-link">
            Login
          </Link>
        </p>

        <p className="text-dark-text-secondary text-xs text-center mt-4">
          By signing up, you agree to our <Link to="/privacy" className="text-dark-accent hover:underline">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
